import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import useNetworkOauthUrls from '@/utils/hooks/signup/useNetworkOauthUrls';
import useSocialProfiles from '@/utils/hooks/swr/useSocialProfiles';
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import { useSocialProfileAccess } from '@/utils/hooks/upsell/useSocialProfileAccess';

import { classNames } from '@/utils';
import { post } from '@/utils/client';
import faqUrl from '@/utils/urls/faqUrl';
import { useSignUpStore } from '@/utils/zustand/providers/sign-up-provider';
import useRouterQuery from '@/hooks/utils/useRouterQuery';
import { BASE } from '@/types/utils/sizesType';
import useStateWithReset from '@/utils/hooks/utils/useStateWithReset';
import useToggle from '@/utils/hooks/utils/useToggle';

import type { SocialProfileNetworkType } from '@/types/socialProfileType';

import { SIGNUP_NEXT_STEP } from '@/lib/routes/apiEndpoints';
import { SIGN_UP } from '@/lib/routes/privateRoutes';
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    THREADS,
    TIKTOK_BUSINESS,
    X,
    YOUTUBE
} from '@/lib/socialProfile';
import {
    PENDO_CLASS_SIGNUP_STEP_ADD_SP_FB,
    PENDO_CLASS_SIGNUP_STEP_ADD_SP_IG,
    PENDO_CLASS_SIGNUP_STEP_ADD_SP_LI,
    PENDO_CLASS_SIGNUP_STEP_ADD_SP_PI,
    PENDO_CLASS_SIGNUP_STEP_ADD_SP_TH,
    PENDO_CLASS_SIGNUP_STEP_ADD_SP_TK,
    PENDO_CLASS_SIGNUP_STEP_ADD_SP_X,
    PENDO_CLASS_SIGNUP_STEP_ADD_SP_YT
} from '@/lib/tools/pendo/class';
import { url_faq_add_instagram_profile } from '@/lib/urls/faqUrls';

import { iconSize } from '@/components/base/Avatar/avatarConfig';
import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner/CompoundBanner';
import Tooltip from '@/components/base/Tooltip';
import { ButtonNetworkSuggestion } from '@/components/composite/Button/ButtonsInsight/ButtonNetworkSuggestion';
import { XIconWithCrown } from '@/components/composite/Icon/XIconWithCrown';
import ErrorAddSingleProfileBanner from '@/components/composite/SocialProfiles/ErrorAddSingleProfileBanner';
import { ProfileConnectUpsellWrapper } from '@/components/composite/Upsell';
import Instagram from '@/components/icons/Instagram';
import { PinterestIcon } from '@/components/icons/Networks/PinterestIcon';
import { YoutubeIcon } from '@/components/icons/Networks/YoutubeIcon';
import SocialProfilesAdded from '@/components/pages/signup/connectSocialProfiles/SocialProfilesAdded';
import SocialProfileConnectButton from '@/components/pages/signup/connectSocialProfiles/SocialProfilesConnect/SocialProfileConnectButton';
import StickyFooter from '@/components/pages/signup/StickyFooter';
import { Trans } from '@/components/tools/translation';

const AddSocialProfilesTutorialModal = dynamic(
    () => import('@/components/pages/signup/AddSocialProfilesTutorialModal')
);

const addSocialProfilesParameter = {
    [INSTAGRAM]: FACEBOOK,
    [FACEBOOK]: FACEBOOK,
    [LINKEDIN]: LINKEDIN
};

const SocialProfilesConnect = () => {
    const { updateCurrentStep } = useSignUpStore((state) => state);
    const { t } = useTranslation('signup_funnel');

    const { query, replace, push } = useRouter();
    const { deleteAllQueries } = useRouterQuery();
    const { errors, oauth_error } = query;

    const { user, mutateUser } = useUser();
    const { socialProfiles } = useSocialProfiles();
    const { hasAccess: hasXAccess } = useSocialProfileAccess({ network: 'x' });
    const { hasAccess: hasThreadsAccess } = useSocialProfileAccess({ network: 'threads' });

    const { isOpen: isOpenTutorial, toggle: toggleTutorial } = useToggle();

    const [typeTutorial, setTypeTutorial] = useState<typeof INSTAGRAM | typeof FACEBOOK>(null);
    const [socialNetworkChosen, setSocialNetworkChosen] = useState<SocialProfileNetworkType>(null);
    const [
        errorAddSingleProfileMessage,
        setErrorAddSingleProfileMessage,
        resetErrorAddSingleProfileMessage
    ] = useStateWithReset<string>(null);
    const [finishState, setFinishState, resetFinishState] = useStateWithReset<{
        isLoading: boolean;
        error: string;
    }>({
        isLoading: false,
        error: null
    });

    const { getOauthUrl } = useNetworkOauthUrls();

    const hasSocialProfiles = Boolean(socialProfiles?.length);

    useEffect(() => {
        if (!errors && !oauth_error) return;
        const errorMessage = typeof errors === 'string' ? errors.split('|')[0] : null;
        setErrorAddSingleProfileMessage((errorMessage || oauth_error).toString());
        replace(deleteAllQueries());
    }, [errors, oauth_error]);

    const previousStep = () => {
        updateCurrentStep('USER_CREATED', user.id);
        push(SIGN_UP.USER_CREATED);
    };

    const finishStep = async () => {
        setFinishState({
            isLoading: true,
            error: null
        });
        try {
            const user_signup_step = user?.sign_up_step ?? '';

            if (user_signup_step !== 'social_profiles_connected') {
                await post(SIGNUP_NEXT_STEP);
                await mutateUser();
            }

            updateCurrentStep('SOCIAL_PROFILES_CONNECTED', user.id);
            await push(SIGN_UP.SOCIAL_PROFILES_CONNECTED);
        } catch (e) {
            setFinishState({
                isLoading: false,
                error: t('errors:error_occured')
            });
        }
    };

    const goToUrl = (network: SocialProfileNetworkType) => {
        // Disable all buttons + add loader
        setSocialNetworkChosen(network);

        // Redirect to the endpoint with params
        window.location.assign(
            getOauthUrl({
                network,
                redirect: {
                    query: {
                        add_social_profiles: addSocialProfilesParameter?.[network]
                    }
                }
            })
        );
    };

    return (
        <div className="mt-5 flex flex-1 flex-col justify-between">
            <div>
                {finishState.error ? (
                    <InfoBanner className="mb-5">
                        <InfoBanner.Icon />
                        <InfoBanner.Content title={t('errors:error')} message={finishState.error} />
                        <InfoBanner.Close onClose={resetFinishState} />
                    </InfoBanner>
                ) : null}
                <ErrorAddSingleProfileBanner
                    className="mb-5"
                    error={errorAddSingleProfileMessage}
                    onClose={resetErrorAddSingleProfileMessage}
                />
                <div className="relative mb-4 ml-12 flex items-center space-x-2 text-xs text-icoGreen sm:ml-20">
                    <span className="absolute -left-4 top-1.5 inline-block h-3.5 w-3.5 rounded-tl-xl border-l-2 border-t-2 border-icoGreen" />
                    <div>
                        <span className="align-middle">
                            {t('connect_social_profiles.connect_via_facebook')}
                        </span>
                        <Tooltip
                            interactive={true}
                            position="top"
                            content={
                                <p className="text-center">
                                    <Trans
                                        i18nKey="signup_funnel:connect_social_profiles.connect_via_facebook_tooltip"
                                        components={{
                                            tag0: (
                                                // eslint-disable-next-line jsx-a11y/anchor-has-content
                                                <a
                                                    key="tooltip"
                                                    target="_blank"
                                                    href={faqUrl(url_faq_add_instagram_profile)}
                                                    className="text-icoBlue"
                                                    rel="help noopener noreferrer"
                                                />
                                            )
                                        }}
                                    />
                                </p>
                            }>
                            <Icon icon="info" className="ml-2 align-middle" />
                        </Tooltip>
                    </div>
                </div>
                <div className="mb-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <SocialProfileConnectButton
                        title={t('connect_social_profiles.social_profile_buttons.instagram.title')}
                        subtitle={t(
                            'connect_social_profiles.social_profile_buttons.instagram.subtitle'
                        )}
                        icon={<Instagram />}
                        className={PENDO_CLASS_SIGNUP_STEP_ADD_SP_IG}
                        disabled={socialNetworkChosen !== null}
                        onClick={() => {
                            setTypeTutorial(INSTAGRAM);
                            toggleTutorial();
                        }}
                    />
                    <SocialProfileConnectButton
                        title={t('connect_social_profiles.social_profile_buttons.facebook.title')}
                        subtitle={t(
                            'connect_social_profiles.social_profile_buttons.facebook.subtitle'
                        )}
                        icon={
                            <Icon
                                icon="facebook"
                                className={`${iconSize[BASE]} text-facebook dark:text-white`}
                            />
                        }
                        className={PENDO_CLASS_SIGNUP_STEP_ADD_SP_FB}
                        disabled={socialNetworkChosen !== null}
                        onClick={() => {
                            setTypeTutorial(FACEBOOK);
                            toggleTutorial();
                        }}
                    />
                    <SocialProfileConnectButton
                        title={t('connect_social_profiles.social_profile_buttons.linkedin.title')}
                        subtitle={t(
                            'connect_social_profiles.social_profile_buttons.linkedin.subtitle'
                        )}
                        icon={
                            <Icon
                                icon="linkedin"
                                className={`${iconSize[BASE]} text-linkedin dark:text-white`}
                            />
                        }
                        className={PENDO_CLASS_SIGNUP_STEP_ADD_SP_LI}
                        disabled={socialNetworkChosen !== null}
                        isLoading={socialNetworkChosen === LINKEDIN}
                        onClick={() => goToUrl(LINKEDIN)}
                    />
                    <SocialProfileConnectButton
                        title={t('connect_social_profiles.social_profile_buttons.tiktok.title')}
                        subtitle={t(
                            'connect_social_profiles.social_profile_buttons.tiktok.subtitle'
                        )}
                        icon={
                            <Icon
                                icon="tiktok"
                                className={`${iconSize[BASE]} text-tiktok dark:text-white`}
                            />
                        }
                        className={PENDO_CLASS_SIGNUP_STEP_ADD_SP_TK}
                        disabled={socialNetworkChosen !== null}
                        isLoading={socialNetworkChosen === TIKTOK_BUSINESS}
                        onClick={() => goToUrl(TIKTOK_BUSINESS)}
                    />
                    <SocialProfileConnectButton
                        title={t('connect_social_profiles.social_profile_buttons.youtube.title')}
                        subtitle={t(
                            'connect_social_profiles.social_profile_buttons.youtube.subtitle'
                        )}
                        icon={<YoutubeIcon />}
                        className={PENDO_CLASS_SIGNUP_STEP_ADD_SP_YT}
                        disabled={socialNetworkChosen !== null}
                        isLoading={socialNetworkChosen === YOUTUBE}
                        onClick={() => goToUrl(YOUTUBE)}
                    />
                    <SocialProfileConnectButton
                        title={t('connect_social_profiles.social_profile_buttons.pinterest.title')}
                        subtitle={t(
                            'connect_social_profiles.social_profile_buttons.pinterest.subtitle'
                        )}
                        icon={<PinterestIcon />}
                        className={PENDO_CLASS_SIGNUP_STEP_ADD_SP_PI}
                        disabled={socialNetworkChosen !== null}
                        isLoading={socialNetworkChosen === PINTEREST}
                        onClick={() => goToUrl(PINTEREST)}
                    />
                    <ProfileConnectUpsellWrapper platform="x" showUpgradeButton={false}>
                        <SocialProfileConnectButton
                            title={t('connect_social_profiles.social_profile_buttons.x.title')}
                            subtitle={t(
                                'connect_social_profiles.social_profile_buttons.x.subtitle'
                            )}
                            icon={<XIconWithCrown className={iconSize[BASE]} />}
                            className={PENDO_CLASS_SIGNUP_STEP_ADD_SP_X}
                            disabled={socialNetworkChosen !== null || !hasXAccess}
                            isLoading={socialNetworkChosen === X}
                            onClick={hasXAccess ? () => goToUrl(X) : undefined}
                        />
                    </ProfileConnectUpsellWrapper>
                    <ProfileConnectUpsellWrapper platform="threads" showUpgradeButton={false}>
                        <SocialProfileConnectButton
                            title={t(
                                'connect_social_profiles.social_profile_buttons.threads.title'
                            )}
                            subtitle={t(
                                'connect_social_profiles.social_profile_buttons.threads.subtitle'
                            )}
                            icon={
                                <Icon
                                    icon="threads"
                                    className={`${iconSize[BASE]} text-threads dark:text-white`}
                                />
                            }
                            className={PENDO_CLASS_SIGNUP_STEP_ADD_SP_TH}
                            disabled={socialNetworkChosen !== null || !hasThreadsAccess}
                            isLoading={socialNetworkChosen === THREADS}
                            onClick={hasThreadsAccess ? () => goToUrl(THREADS) : undefined}
                        />
                    </ProfileConnectUpsellWrapper>
                    <ButtonNetworkSuggestion
                        disabled={socialNetworkChosen !== null}
                        className="relative flex flex-1 items-center gap-3 font-GSemiBold text-sm">
                        <Icon
                            icon="ideas"
                            className={classNames('text-base text-icoBlue', {
                                'opacity-40': socialNetworkChosen !== null
                            })}
                        />
                    </ButtonNetworkSuggestion>
                </div>
                <div className="mb-10 rounded-md bg-icoGray-100 p-6 xs:px-10 xs:py-8 sm:px-12 sm:py-10 lg:hidden">
                    <SocialProfilesAdded />
                </div>
            </div>
            <StickyFooter wrapperClassName="justify-between">
                <button
                    type="button"
                    onClick={previousStep}
                    className="font-GSemiBold text-xs text-icoGray-500 underline">
                    {t('commons:back')}
                </button>
                <Button
                    disabled={!hasSocialProfiles || socialNetworkChosen !== null}
                    onClick={finishStep}
                    isLoading={finishState.isLoading}
                    data-testid="signup-confirm-button">
                    {t('commons:confirm')}
                </Button>
            </StickyFooter>
            <AddSocialProfilesTutorialModal
                isOpen={isOpenTutorial}
                toggle={toggleTutorial}
                type={typeTutorial}
                callback={() => goToUrl(FACEBOOK)}
            />
        </div>
    );
};

export default SocialProfilesConnect;
